// Global axios resilience — registered ONCE at app bootstrap.
//
// Because business/customer components call the default `axios` instance (a global
// axios.defaults.baseURL is already configured), a single global response
// interceptor covers them all. It:
//   - marks the app "degraded" on a 503 / service_unavailable / network failure
//   - marks the app "healthy" again on the next successful API response
//   - retries idempotent GETs a few times with exponential backoff before failing
//   - NEVER fabricates a successful response — failures still reject so callers
//     show real error state instead of fake/sample data.
//
// SuperAdmin uses its own axios.create() instances; those are out of scope here
// and can opt in later by importing the helpers from serviceHealth.js.
import axios from 'axios';
import {
  markDegraded,
  markHealthy,
  isServiceUnavailable,
  backoffDelayMs,
  sleep,
} from './serviceHealth';
import { forceRefreshIdToken } from './firebase';

const MAX_RETRIES = 3;

let _installed = false;

export function installAxiosResilience() {
  if (_installed) return;
  _installed = true;

  axios.interceptors.response.use(
    (response) => {
      markHealthy();
      return response;
    },
    async (error) => {
      const config = error.config || {};
      const method = (config.method || 'get').toLowerCase();
      const status = error.response?.status;

      // DR-013: on an authentication 401, do ONE single-flight forced token refresh and retry the
      // request EXACTLY once with the fresh token. No recursion (guarded by __authRetried), no refresh
      // stampede (forceRefreshIdToken is single-flight), never force-logout here — a still-401 after the
      // retry rejects so the caller shows a real state. `token_invalid` still gets one retry then rejects.
      if (status === 401 && !config.__authRetried && !config.__isRefreshCall) {
        config.__authRetried = true;
        const fresh = await forceRefreshIdToken();
        if (fresh) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${fresh}`;
          markHealthy();
          return axios(config);
        }
      }

      const transient = isServiceUnavailable(error);

      if (transient) {
        markDegraded(
          error.response ? 'service_unavailable' : 'backend_unreachable'
        );

        // Only retry idempotent reads; never retry writes (POST/PUT/PATCH/DELETE).
        if (method === 'get') {
          config.__retryCount = (config.__retryCount || 0) + 1;
          if (config.__retryCount <= MAX_RETRIES) {
            await sleep(backoffDelayMs(config.__retryCount));
            return axios(config);
          }
        }
      }

      // Always reject — callers must surface real failure, not fake success.
      return Promise.reject(error);
    }
  );
}

export default installAxiosResilience;
